import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  TextField,
  TablePagination,
  Button,
  Box,
  InputAdornment,
  Modal,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { viewImage, viewImageOnNewTab } from "../../../utils/viewPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

const TenderPurchaseTable = ({ vendors, isLoading, getTenderPurchase, tab }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [status, setStatus] = useState();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [btnLoading, setBtnLoading] = useState(false);

  const handleOpen = (s) => {
    setOpen(true);
    setStatus(s);
  };
  const handleClose = () => {
    setOpen(false);
    setStatus("");
  };

  const handleSelectAllClick = (event, tenderVendors) => {
    const vtIds = tenderVendors.map((v) => v.vtId);

    if (event.target.checked) {
      // Select all: merge existing selected with new ones and remove duplicates
      const newSelected = Array.from(new Set([...selected, ...vtIds]));
      setSelected(newSelected);
    } else {
      // Deselect all: remove only the tender-specific ids
      const filtered = selected.filter((id) => !vtIds.includes(id));
      setSelected(filtered);
    }
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const paginatedData = useMemo(() => {
    const filtered = (vendors || []).filter(
      (vendor) =>
        vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.utr?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [vendors, page, rowsPerPage, searchTerm]);

  const handleVendorTenderUpdate = async () => {
    setBtnLoading(true)
    try {
      const payload = selected.map((id) => ({
        vtId: id,
        status: status,
        username: storedUser.username,
      }));
      
      if(storedUser.userRole[0] === "SUPERADMIN"){
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/vendor-tender/by-superadmin`,
          payload,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
        );
      } else {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/vendor-tender`,
          payload,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
        );
      }

      setSelected([]);
      setOpen(false);
      getTenderPurchase();
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false)
    }
  };

  const columnWidth = `${tab === 1 ? 100 / 5 : 100 / 4}%`;

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}   
      <Paper
        sx={{ width: "100%", overflow: "hidden", boxShadow: "none !important" }}
      >
        <Box
          display={"flex"}
          justifyContent={tab === 1 ? "space-between" : "end"}
          alignItems={"center"}
        >
          {(storedUser.userRole[0] === "FINANCE_HEAD" || storedUser.userRole[0] === "SUPERADMIN") && tab === 1 && (
            <Box>
              <Button
                className="accept-all"
                onClick={() => handleOpen("SHORTLISTED")}
              >
                Approve Selected
              </Button>
              <Button
                className="reject-all"
                onClick={() => handleOpen("DISQUALIFIED")}
              >
                Reject Selected
              </Button>
            </Box>
          )}
          <Box
            display="flex"
            justifyContent="end"
            alignItems="center"
            gap={1}
            p={2}
          >
            <TextField
              placeholder="Search"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: 300,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
                "&:hover": {
                  borderRadius: "12px",
                  background: "#D8E9F0",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {/* <Button
              startIcon={<FilterListIcon />}
              sx={{
                height: 40,
                border: "1px solid #c7c7c7 !important",
                borderRadius: "12px",
                color: "#000",
                textTransform: "none",
                backgroundColor: "none",
                ":hover": {
                  backgroundColor: "none",
                },
              }}
              onClick={() => {
                // Optional: Toggle a filter modal/panel if needed
                console.log("Filter button clicked");
              }}
            >
              Filter
            </Button> */}
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {tab === 1 && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selected.length > 0 &&
                        selected.length <
                          Object.values(paginatedData || {}).flat().length
                      }
                      checked={
                        selected.length > 0 &&
                        selected.length ===
                          Object.values(paginatedData || {}).flat().length
                      }
                      onChange={(event) =>
                        handleSelectAllClick(
                          event,
                          Object.values(paginatedData || {}).flat()
                        )
                      }
                    />
                  </TableCell>
                )}
                <TableCell sx={{ width: columnWidth }}>Name</TableCell>
                <TableCell sx={{ width: columnWidth }}>Purchase Amount</TableCell>
                <TableCell sx={{ width: columnWidth }}>UTR</TableCell>
                <TableCell sx={{ width: columnWidth }}>
                  Uploaded Document
                </TableCell>
                {tab === 1 && (
                  <TableCell sx={{ width: columnWidth }}>
                    Mark As Received
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No rows
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => {
                  const isItemSelected = isSelected(row.vtId);
                  return (
                    <TableRow
                      key={row.veId}
                      hover
                      role="checkbox"
                      selected={isItemSelected}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#EAF4FF" : "#ffffff",
                      }}
                    >
                      {tab === 1 && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleClick(row.vtId)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <strong>{row.name}</strong>
                      </TableCell>
                      <TableCell>{row.purchaseAmount}</TableCell>
                      <TableCell>{row.utr}</TableCell>
                      <TableCell>
                        {row.document ?
                        <Button
                          className="btn-contained"
                          sx={{margin: "0 !important", width: '0 !important'}}
                          onClick={() => viewImageOnNewTab(row.document, storedUser)}
                        >
                          View
                        </Button> : "Not uploaded"}
                      </TableCell>
                      {tab === 1 && (
                        <TableCell>
                          <Button
                            className="btn-contained"
                            sx={{ margin: "0 !important", fontSize: "12px" }}
                            onClick={() => {
                              handleClick(row.vtId);
                              handleOpen("SHORTLISTED");
                            }}
                          >
                            Mark as Received
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={
            (vendors || []).filter(
              (vendor) =>
                vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                vendor.utr?.toLowerCase().includes(searchTerm.toLowerCase())
            ).length
          }
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={10}
          rowsPerPageOptions={[]}
        />

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography
              id="modal-modal-title"
              variant="h5"
              fontWeight={600}
              mb={2}
            >
              Mark Purchase as Received?
            </Typography>
            <Typography>
              Please ensure you have checked the details carefully. <br />
              This action is not reversible.
            </Typography>
            <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
              <Button className="btn-outlined btn-reject" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                className="btn-contained"
                onClick={handleVendorTenderUpdate}
                disabled={btnLoading}
              >
                {btnLoading ? "Confirming" : "Confirm"}
              </Button>
            </Box>
          </Box>
        </Modal>
      </Paper>
    </div>
  );
};

export default TenderPurchaseTable;
